/**
 * View-model helpers for normalized listings (Milestone 2).
 */
import {
  AVAILABILITY_STATUS,
  LISTING_BODY_TYPE,
  LISTING_FALLBACK_COPY,
  LISTING_FUEL_TYPE,
  LISTING_PLACEHOLDER_IMAGE,
  LISTING_TRANSMISSION,
  PUBLIC_STATUS,
} from "../../constants/automapListingConstants";
import {
  formatAvailabilityStatus,
  formatBodyType,
  formatFairPriceLabel,
  formatFuelType,
  formatFreshnessLabel,
  formatLastConfirmedAt,
  formatLocationLabel,
  formatMileageKmLong,
  formatPriceCop,
  formatRiskLevel,
  formatSpecsRow,
  formatTransmission,
  formatVehicleTitle,
  formatVehicleYear,
  getDealerInitials,
} from "./listingFormatters";
import { listingHasDealerGap, getListingPublicStatus } from "./listingVisibility";
import { getVehicleDetailHref } from "./listingDetailRoutes";
import { getPublicVehicleDetailPath } from "../../seo/canonicalUrls";
import { resolveRiskLevel, getRiskLevelLabel } from "../vehicleRiskViewModel";
import { formatFairPriceLabel as formatFairPriceLabelFromVm, resolveFairPriceLabel } from "../fairPriceViewModel";
import { getDealerTrustViewModel } from "../dealerTrustViewModel";
import { getVehicleDetailSeo } from "../../seo/vehicleSeo";
import { PUBLIC_SEARCH_PATH } from "../../seo/seoConstants";
import {
  formatCardAvailabilityStatus,
  formatCardFreshnessLabel,
  formatCardLastConfirmed,
  getUnknownAttributeLabel,
} from "../../intl/automotiveCardCopy";
import { UNKNOWN_ATTRIBUTE_LABEL } from "./listingFormatters";

function resolveUnknownAttributeLabel(t) {
  return t ? getUnknownAttributeLabel(t) : UNKNOWN_ATTRIBUTE_LABEL;
}

function resolveTransmissionLabel(listing, t, unknownLabel) {
  const transmission = listing?.transmission;
  if (!transmission || transmission === LISTING_TRANSMISSION.UNKNOWN) return unknownLabel;
  if (t) {
    const key = `automotive.listing.transmission.${transmission}`;
    const value = t(key);
    if (value !== key) return value;
  }
  return formatTransmission(transmission, unknownLabel);
}

function resolveBodyTypeLabel(listing, t, unknownLabel) {
  const bodyType = listing?.bodyType;
  if (!bodyType || bodyType === LISTING_BODY_TYPE.UNKNOWN) return unknownLabel;
  if (t) {
    const key = `automotive.listing.body.${bodyType}`;
    const value = t(key);
    if (value !== key) return value;
  }
  return formatBodyType(bodyType, unknownLabel);
}

function resolveFuelTypeLabel(listing, t, unknownLabel) {
  const fuelType = listing?.fuelType;
  if (!fuelType || fuelType === LISTING_FUEL_TYPE.UNKNOWN) return unknownLabel;
  if (t) {
    const key = `automotive.listing.fuel.${fuelType}`;
    const value = t(key);
    if (value !== key) return value;
  }
  return formatFuelType(fuelType, unknownLabel);
}

export function buildListingDetailHref(slug, listing, locale = null) {
  return getVehicleDetailHref(listing, slug, { locale });
}

export function toVehicleCardViewModel(
  listing,
  { slug = "automap", currency = "COP", formatMoney = null, t = null, locale = "es-CO" } = {}
) {
  if (!listing) {
    const unknownLabel = resolveUnknownAttributeLabel(t);
    return {
      href: "#",
      title: "Vehículo",
      priceLabel: t ? t("automotive.card.priceUnavailable") : LISTING_FALLBACK_COPY.price,
      mileageLabel: unknownLabel,
      yearLabel: unknownLabel,
      locationLabel: t ? t("automotive.card.locationUnavailable") : LISTING_FALLBACK_COPY.location,
      primaryImageUrl: LISTING_PLACEHOLDER_IMAGE,
      dealerName: t ? t("automotive.card.dealerPending") : LISTING_FALLBACK_COPY.dealer,
      dealerInitials: "AM",
      dealerVerified: false,
      fairPriceLabel: LISTING_FALLBACK_COPY.fairPrice,
      riskLevel: listing?.riskLevel || "unknown",
      riskLabel: LISTING_FALLBACK_COPY.risk,
      availabilityLabel: LISTING_FALLBACK_COPY.confirmation,
      freshnessLabel: LISTING_FALLBACK_COPY.confirmation,
      specsRowLabel: formatSpecsRow({}, unknownLabel),
      listingSlug: null,
      dealerProfileHref: null,
      whatsappAvailable: false,
      canContact: false,
      ctaLabel: "WhatsApp",
      bodyTypeLabel: unknownLabel,
      fuelTypeLabel: unknownLabel,
      transmissionLabel: unknownLabel,
      hasDetailLink: false,
    };
  }

  const unknownLabel = resolveUnknownAttributeLabel(t);
  const dealer = toDealerContactViewModel(listing, null, { slug });
  const detailHref = buildListingDetailHref(slug, listing, locale);
  const resolvedRiskLevel = resolveRiskLevel(listing);
  const transmissionLabel = resolveTransmissionLabel(listing, t, unknownLabel);
  const bodyTypeLabel = resolveBodyTypeLabel(listing, t, unknownLabel);

  return {
    href: detailHref,
    hasDetailLink: Boolean(listing.id),
    listingSlug: listing.slug || listing.id || null,
    title: formatVehicleTitle(listing),
    priceLabel: formatPriceCop(listing.priceCop, currency, { formatMoney }),
    mileageLabel: formatMileageKmLong(listing.mileageKm, unknownLabel),
    yearLabel: formatVehicleYear(listing.year, unknownLabel),
    locationLabel: formatLocationLabel(listing),
    specsRowLabel: formatSpecsRow(listing, unknownLabel),
    primaryImageUrl: listing.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE,
    dealerName: listing.dealerName || (t ? t("automotive.card.dealerPending") : LISTING_FALLBACK_COPY.dealer),
    dealerInitials: getDealerInitials(listing.dealerName),
    dealerVerified: dealer.isVerified,
    dealerProfileHref: dealer.profileHref,
    dealerResponseTimeLabel: dealer.dealerResponseTimeLabel,
    dealerCityLabel: dealer.dealerCity,
    whatsappAvailable: dealer.canContact,
    canContact: dealer.canContact,
    fairPriceLabel: t
      ? formatFairPriceLabelFromVm(resolveFairPriceLabel(listing), { t })
      : formatFairPriceLabel(listing.fairPriceLabel),
    riskLevel: resolvedRiskLevel,
    riskLabel: getRiskLevelLabel(resolvedRiskLevel, { t }),
    availabilityLabel: t
      ? formatCardAvailabilityStatus(listing.availabilityStatus, t)
      : formatAvailabilityStatus(listing.availabilityStatus),
    freshnessLabel: t ? formatCardFreshnessLabel(listing, t) : formatFreshnessLabel(listing),
    lastConfirmedLabel: t
      ? formatCardLastConfirmed(listing.lastConfirmedAt, t, locale)
      : formatLastConfirmedAt(listing.lastConfirmedAt),
    ctaLabel: "WhatsApp",
    bodyTypeLabel,
    fuelTypeLabel: resolveFuelTypeLabel(listing, t, unknownLabel),
    transmissionLabel,
    hasDealerGap: listingHasDealerGap(listing),
    featuredPlacement: Boolean(listing.placementId || listing.featuredPlacement),
  };
}

export function toDealerContactViewModel(listing, dealerOverride = null, { slug = "automap", t = null } = {}) {
  return getDealerTrustViewModel(listing, dealerOverride, { slug, t });
}

export function toListingSeoViewModel(listing, { slug = "automap", dealer = null, pageStatus = "ready" } = {}) {
  const seo = getVehicleDetailSeo(listing, dealer, {
    pageStatus,
    searchHref: PUBLIC_SEARCH_PATH,
  });

  return {
    title: listing?.seoTitle || seo.title,
    description: listing?.seoDescription || seo.description,
    canonicalHref: seo.canonicalPath || (listing ? getPublicVehicleDetailPath(listing) : null),
    canonicalUrl: seo.canonicalUrl,
    imageUrl: seo.openGraph?.image || listing?.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE,
    noindexRecommended: seo.noindex,
    robots: seo.robots,
    openGraph: seo.openGraph,
    twitter: seo.twitter,
    breadcrumbs: seo.breadcrumbs,
    structuredData: seo.structuredData,
  };
}

export function toListingAnalyticsPayload(listing, extra = {}) {
  if (!listing) return { ...extra };
  return {
    listingId: listing.id,
    dealerId: listing.dealerId,
    vehicleTitle: formatVehicleTitle(listing),
    make: listing.make,
    model: listing.model,
    year: listing.year,
    priceCop: listing.priceCop,
    city: listing.city,
    bodyType: listing.bodyType,
    transmission: listing.transmission,
    fairPriceLabel: listing.fairPriceLabel,
    riskLevel: resolveRiskLevel(listing),
    availabilityStatus: listing.availabilityStatus,
    listingStatus: listing.status,
    publicStatus: listing.publicStatus,
    sourcePage: extra.sourcePage,
    sourceComponent: extra.sourceComponent,
    ...extra,
  };
}

export function toVehicleDetailViewModel(
  listing,
  { slug = "automap", currency = "COP", similarListings = [], dealerOverride = null, t = null, locale = "es-CO", formatMoney = null } = {}
) {
  const card = toVehicleCardViewModel(listing, { slug, currency, t, locale, formatMoney });
  const dealer = toDealerContactViewModel(listing, dealerOverride, { slug, t });
  const seo = toListingSeoViewModel(listing, { slug, dealer: dealerOverride });

  const labels = t
    ? {
        make: t("automotive.make"),
        model: t("automotive.model"),
        year: t("automotive.year"),
        version: t("automotive.detail.layout.version"),
        mileage: t("automotive.mileage"),
        transmission: t("automotive.transmission"),
        fuelType: t("automotive.fuelType"),
        bodyType: t("automotive.search.filterStackBodyType"),
        color: t("automotive.condition"),
        engine: t("automotive.detail.layout.engine"),
        drivetrain: t("automotive.detail.layout.drivetrain"),
        doors: t("automotive.detail.layout.doors"),
        location: t("automotive.location"),
        type: t("automotive.detail.layout.type"),
      }
    : {
        make: "Marca",
        model: "Modelo",
        year: "Año",
        version: "Versión",
        mileage: "Kilometraje",
        transmission: "Transmisión",
        fuelType: "Combustible",
        bodyType: "Carrocería",
        color: "Color",
        engine: "Motor",
        drivetrain: "Tracción",
        doors: "Puertas",
        location: "Ciudad",
        type: "Tipo",
      };

  const optionalSpecs = [
    listing?.make ? { label: labels.make, value: listing.make } : null,
    listing?.model ? { label: labels.model, value: listing.model } : null,
    { label: labels.year, value: card.yearLabel },
    listing?.trim || listing?.version
      ? { label: labels.version, value: listing.trim || listing.version }
      : null,
    { label: labels.mileage, value: card.mileageLabel },
    { label: labels.transmission, value: card.transmissionLabel },
    { label: labels.fuelType, value: card.fuelTypeLabel },
    { label: labels.bodyType, value: card.bodyTypeLabel },
    listing?.color ? { label: labels.color, value: listing.color } : null,
    listing?.engine ? { label: labels.engine, value: listing.engine } : null,
    listing?.drivetrain ? { label: labels.drivetrain, value: listing.drivetrain } : null,
    listing?.doors != null ? { label: labels.doors, value: String(listing.doors) } : null,
    { label: labels.location, value: card.locationLabel },
    listing?.plateLastDigit
      ? {
          label: t ? t("automotive.vinOrPlate") : "Placa",
          value: t
            ? t("automotive.detail.layout.plateEndsIn", { digit: listing.plateLastDigit })
            : `Termina en ${listing.plateLastDigit}`,
        }
      : null,
  ].filter((row) => row && row.value);

  const specRows =
    optionalSpecs.length > 0
      ? optionalSpecs
      : [
          { label: labels.year, value: card.yearLabel },
          { label: labels.mileage, value: card.mileageLabel },
          { label: labels.transmission, value: card.transmissionLabel },
          { label: labels.fuelType, value: card.fuelTypeLabel },
          { label: labels.type, value: card.bodyTypeLabel },
          { label: labels.location, value: card.locationLabel },
        ];

  const unknownLabel = resolveUnknownAttributeLabel(t);

  return {
    ...card,
    subtitle: [
      formatVehicleYear(listing?.year, unknownLabel),
      listing?.city,
      formatBodyType(listing?.bodyType, unknownLabel),
    ]
      .filter(Boolean)
      .join(" · "),
    specRows,
    galleryImages: listing?.images?.length ? listing.images : [card.primaryImageUrl],
    dealerCard: dealer,
    fairPriceSection: {
      label: card.fairPriceLabel,
      confidence: listing?.fairPriceConfidence || null,
      sampleSize: listing?.fairPriceSampleSize ?? null,
      deltaCop: listing?.fairPriceDeltaCop ?? null,
    },
    riskSection: {
      level: card.riskLevel,
      label: card.riskLabel,
      flags: listing?.riskFlags || [],
    },
    availabilitySection: {
      status: listing?.availabilityStatus,
      label: card.availabilityLabel,
      lastConfirmedLabel: (() => {
        if (listing?.lastConfirmedAt) return card.lastConfirmedLabel;
        if (listing?.availabilityStatus === AVAILABILITY_STATUS.PENDING_CONFIRMATION) {
          return card.freshnessLabel;
        }
        if (card.lastConfirmedLabel === card.availabilityLabel) return null;
        return card.lastConfirmedLabel;
      })(),
    },
    similarListings,
    seo,
    analyticsPayload: toListingAnalyticsPayload(listing, {
      sourcePage: "vehicle_detail",
      listingSlug: listing?.slug || listing?.id || null,
      dealerName: dealer.dealerName,
    }),
    listing,
    publicStatus: listing ? getListingPublicStatus(listing) : PUBLIC_STATUS.NOT_FOUND,
    canContact: dealer.canContact,
  };
}
